import { SearchOutlined } from "@ant-design/icons";
import { Button, Input, InputRef, Space } from "antd";
import { ColumnType } from "antd/lib/table";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

interface ColumnSearchOptions<T> {
    placeholder: string;
    /**
     * Every string a record should be searchable by. Returning the record's
     * translations alongside its default-locale value lets an admin find a row
     * by any language it is published in.
     */
    getSearchValues: (record: T) => Array<string | null | undefined>;
}

/**
 * The Ant Design filter-dropdown boilerplate every CRUD table repeats, as a
 * single reusable column fragment.
 */
export function useColumnSearch<T>({
    placeholder,
    getSearchValues
}: ColumnSearchOptions<T>): ColumnType<T> {
    const { t } = useTranslation();
    const searchInput = useRef<InputRef>(null);

    return {
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={placeholder}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => confirm()}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => confirm()}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        {t("actions.search")}
                    </Button>
                    <Button
                        onClick={() => clearFilters && clearFilters()}
                        size="small"
                        style={{ width: 90 }}
                    >
                        {t("actions.reset")}
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) => {
            const needle = (value as string).toLowerCase();
            return getSearchValues(record).some(
                candidate => candidate != null && candidate.toLowerCase().includes(needle)
            );
        },
        onFilterDropdownVisibleChange: visible => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        }
    };
}
